import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Popover,
  Box,
  Stack,
  Paper,
  TextField,
  Checkbox,
  Typography,
  Button,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import TableSortLabel from "@mui/material/TableSortLabel";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckSharpIcon from "@mui/icons-material/CheckSharp";
import QuestionMarkSharpIcon from "@mui/icons-material/QuestionMarkSharp";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Check as AllEcrfCompleted,
  PriorityHigh as EcrfHasIssue,
  Remove as NotEcrfTouched,
  Circle as VisitStatusIcon,
  HelpOutline as HelpIcon,
} from "@mui/icons-material";
import Loader from "../Loader";
import useDeleteQueryMutation from "../../hooks/useDeleteQueryMutation";
import { ECRF_STATUSES, VISIT_STATUSES } from "../../utils/constants";
import { formatDate } from "../../utils/helpers";

const headerCellSx = {
  backgroundColor: (theme) => theme.palette.grey[100],
  fontWeight: "bold",
  textTransform: "uppercase",
  fontSize: "0.85rem",
  color: "#424242",
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  whiteSpace: "nowrap",
};

const groupHeaderCellSx = {
  ...headerCellSx,
  fontSize: "0.95rem",
  fontWeight: 800,
};

// Sticky column left offsets (col1=checkbox, col2=visit name, col3=block name)
const STICKY_LEFT = { col1: 0, col2: 58, col3: 318 };

const stickyHeaderCellSx = (col) => ({
  position: "sticky",
  left: STICKY_LEFT[col],
  zIndex: 4,
  backgroundColor: (theme) => theme.palette.grey[100],
});

const stickyBodyCellSx = (col) => ({
  position: "sticky",
  left: STICKY_LEFT[col],
  zIndex: 1,
  backgroundColor: "white",
});

const sectionStartSx = {
  borderLeft: "2px solid",
  borderLeftColor: "divider",
};

const QueryTableContainer = ({
  allChecked,
  someChecked,
  handleParentChange,
  searchObject,
  setSearchObject,
  downdownVisitsStatus,
  queryLogData,
  checked,
  handleChildChange,
  convertFirstLetterToCapital,
  handleHyperLink,
  handleGenerateQueryMessage,
  setShowChatModal,
  handleGenerateQuery,
  setShowRolesModal,
  QuestionMarkRoundedIcon,
  role,
  isTrialLocked = false,
  setShowModal,
  setInputType,
  blocksSelected,
  isLoading,
}) => {
  const { _id_: patientId } = useParams();
  const deleteQueryMutation = useDeleteQueryMutation();

  const [sortStatus, setSortStatus] = useState(false);
  const [queryStatusCount, setQueryStatusCount] = useState("0");
  const [sortedQueryLogData, setSortedQueryLogData] = useState([]);
  const [deleteQuery, setDeleteQuery] = useState(false);
  const [deleteQueryId, setDeleteQueryId] = useState(false);

  // Popover states
  const [anchorEl, setAnchorEl] = useState(null);
  const handlePopoverOpen = (event) => setAnchorEl(event.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);
  const openPopOver = Boolean(anchorEl);

  const handleSort = () => {
    setSortStatus((prev) => !prev);
    if (sortStatus) {
      const inCompleteQueries = queryLogData.filter(
        (item) => item?.query_status?.status === "InComplete"
      );
      const completeQueries = queryLogData.filter(
        (item) => item?.query_status?.status === "Complete"
      );
      const queryStatusFalseQueries = queryLogData.filter((item) => item?.query_status === false);
      const notStartedQueries = queryLogData.filter((item) => item?.status === "Not Started");
      setSortedQueryLogData([
        ...inCompleteQueries,
        ...completeQueries,
        ...queryStatusFalseQueries,
        ...notStartedQueries,
      ]);
    } else {
      setSortedQueryLogData(queryLogData);
    }
  };

  const checkSelectedBlocksStatus = (statusType) => {
    const selectedItems = queryLogData.filter((item) => blocksSelected.includes(item.id));
    const firstStatus = selectedItems[0]?.status_object?.[statusType];
    const allSameStatus = selectedItems.every(
      (item) => item?.status_object?.[statusType] === firstStatus
    );
    return allSameStatus;
  };

  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const validateInvCertification = () => {
    if (!blocksSelected || blocksSelected.length === 0) {
      setErrorMessage("Please select blocks to update.");
      setErrorPopupOpen(true);
      return false;
    }

    const notCompleteBlocks = blocksSelected.some((id) => {
      const item = queryLogData.find((item) => item.id === id);
      return item?.status_object?.cro_block_status_bool !== true;
    });

    if (notCompleteBlocks) {
      setErrorMessage(
        "Selected entries contain InComplete blocks which cannot be certified. Select only Completed blocks to certify."
      );
      setErrorPopupOpen(true);
      return false;
    }

    const hasCertifiedUncertifiedMixture = !checkSelectedBlocksStatus("inv_certified_bool");

    if (hasCertifiedUncertifiedMixture) {
      setErrorMessage(
        "Selected entries contain a mixture of Certified and Uncertified blocks. Select either only Certified or only Uncertified blocks to update status."
      );
      setErrorPopupOpen(true);
      return false;
    }

    return true;
  };

  const validateDmFreeze = () => {
    if (!blocksSelected || blocksSelected.length === 0) {
      setErrorMessage("Please Select Blocks To Update!");
      setErrorPopupOpen(true);
      return false;
    }

    const notCertifiedBlocks = blocksSelected.some((id) => {
      const item = queryLogData.find((item) => item.id === id);
      return item?.status_object?.inv_certified_bool !== true;
    });

    if (notCertifiedBlocks) {
      setErrorMessage(
        "Selected blocks must be certified by Investigator before they can be frozen."
      );
      setErrorPopupOpen(true);
      return false;
    }

    return true;
  };

  const confirmDelete = (id) => {
    if (id) {
      deleteQueryMutation.mutate(id, {
        onSuccess: () => {
          toast.success("Query Deleted Successfully");
        },
        onError: () => {
          toast.error("Something Went Wrong");
        },
      });
      setDeleteQuery(false);
    }
  };

  useEffect(() => {
    const count = queryLogData.filter((item) => item?.query_status?.status === "InComplete").length;
    setQueryStatusCount(`${count}`);

    if (sortStatus) {
      const inCompleteQueries = queryLogData.filter(
        (item) => item?.query_status?.status === "InComplete"
      );
      const completeQueries = queryLogData.filter(
        (item) => item?.query_status?.status === "Complete"
      );
      const queryStatusFalseQueries = queryLogData.filter((item) => item?.query_status === false);
      const notStartedQueries = queryLogData.filter((item) => item?.status === "Not Started");
      setSortedQueryLogData([
        ...inCompleteQueries,
        ...completeQueries,
        ...queryStatusFalseQueries,
        ...notStartedQueries,
      ]);
    } else {
      setSortedQueryLogData(queryLogData);
    }
  }, [queryLogData]);

  const renderVisitItem = (visitId) => {
    const visitStatus = downdownVisitsStatus?.find((dropdownVisit) => visitId === dropdownVisit.id);

    let ecrfStatusIcon = <></>;
    let visitStatusIcon = <></>;

    if (visitStatus) {
      if (visitStatus.ecrf_status === ECRF_STATUSES.COMPLETED) {
        ecrfStatusIcon = <AllEcrfCompleted sx={{ color: "green", ml: 2, fontSize: 16 }} />;
      } else if (visitStatus.ecrf_status === ECRF_STATUSES.NEUTRAL) {
        ecrfStatusIcon = <NotEcrfTouched sx={{ color: "grey", ml: 2, fontSize: 16 }} />;
      } else {
        ecrfStatusIcon = <EcrfHasIssue sx={{ color: "red", ml: 2, fontSize: 16 }} />;
      }

      if (visitStatus.visit_number !== -4) {
        if (visitStatus.overall_visit_status === VISIT_STATUSES.NOT_OPENED) {
          visitStatusIcon = <VisitStatusIcon sx={{ color: "grey", ml: 2, fontSize: 12 }} />;
        } else if (visitStatus.overall_visit_status === VISIT_STATUSES.INCOMPLETE) {
          visitStatusIcon = <VisitStatusIcon sx={{ color: "red", ml: 2, fontSize: 12 }} />;
        } else if (visitStatus.overall_visit_status === VISIT_STATUSES.SKIPPED) {
          visitStatusIcon = <VisitStatusIcon sx={{ color: "orange", ml: 2, fontSize: 12 }} />;
        } else if (visitStatus.overall_visit_status === VISIT_STATUSES.COMPLETED) {
          visitStatusIcon = <VisitStatusIcon sx={{ color: "green", ml: 2, fontSize: 12 }} />;
        }
      }
    }

    return (
      <>
        {visitStatus.visit_name} {ecrfStatusIcon} {visitStatusIcon}
      </>
    );
  };

  const isDmUpdateDisabled =
    blocksSelected.length > 0 &&
    blocksSelected.every((id) => {
      const item = queryLogData.find((item) => item.id === id);
      return item?.status_object?.inv_certified_bool === true;
    });

  const isInvUpdateDisabled =
    blocksSelected.length > 0 &&
    blocksSelected.every((id) => {
      const item = queryLogData.find((item) => item.id === id);
      return item?.status_object?.dm_freeze_bool === true;
    });

  const updateStatusBtn = (allowedRoles, onClick, disabled = false, testId) => {
    if (!allowedRoles.includes(role)) return null;
    return (
      <Button
        variant="contained"
        size="small"
        sx={{ ml: 1 }}
        onClick={onClick}
        disabled={disabled || isTrialLocked}
        {...(testId ? { "data-testid": testId } : {})}
      >
        Update Status
      </Button>
    );
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer component={Paper} variant="outlined">
        <Table stickyHeader size="small">
          {/* ── Group header row ── */}
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...groupHeaderCellSx,
                  ...stickyHeaderCellSx("col1"),
                  width: 58,
                }}
                align="center"
                colSpan={1}
              />
              <TableCell
                sx={{
                  ...groupHeaderCellSx,
                  ...stickyHeaderCellSx("col2"),
                  minWidth: 260,
                }}
                align="center"
                colSpan={2}
              />
              <TableCell sx={groupHeaderCellSx} align="center" />
              <TableCell sx={groupHeaderCellSx} align="center" />

              <TableCell
                sx={{ ...groupHeaderCellSx, ...sectionStartSx }}
                align="center"
                colSpan={3}
              >
                CRC/INV/SUB-INV
              </TableCell>

              <TableCell
                sx={{
                  ...groupHeaderCellSx,
                  ...sectionStartSx,
                  position: "relative",
                }}
                align="center"
                colSpan={3}
              >
                CRO/DM CDQC
                <Box
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  {updateStatusBtn(
                    ["CRO Admin", "Data Manager"],
                    () => {
                      if (!blocksSelected || blocksSelected.length === 0) {
                        toast.error("Please Select Blocks To Update!");
                      } else {
                        setShowModal(true);
                        setInputType("cro_status");
                      }
                    },
                    isDmUpdateDisabled,
                    "cdqc-update-status-btn"
                  )}
                </Box>
              </TableCell>

              <TableCell
                sx={{
                  ...groupHeaderCellSx,
                  ...sectionStartSx,
                  position: "relative",
                }}
                align="center"
                colSpan={4}
              >
                INV eSignature
                <Box
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  {updateStatusBtn(
                    ["Investigator"],
                    () => {
                      if (validateInvCertification()) {
                        setShowModal(true);
                        setInputType("eSig");
                      }
                    },
                    isInvUpdateDisabled,
                    "inv-esig-update-status-btn"
                  )}
                </Box>
              </TableCell>

              <TableCell
                sx={{
                  ...groupHeaderCellSx,
                  ...sectionStartSx,
                  position: "relative",
                }}
                align="center"
                colSpan={3}
              >
                DM Freeze
                <Box
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  {updateStatusBtn(
                    ["Data Manager"],
                    () => {
                      if (validateDmFreeze()) {
                        setShowModal(true);
                        setInputType("freeze");
                      }
                    },
                    false,
                    "dm-freeze-update-status-btn"
                  )}
                </Box>
              </TableCell>
            </TableRow>

            {/* ── Column header / filter row ── */}
            <TableRow>
              <TableCell
                sx={{
                  ...headerCellSx,
                  ...stickyHeaderCellSx("col1"),
                  width: 58,
                }}
                align="center"
              >
                <Checkbox
                  checked={allChecked}
                  indeterminate={!allChecked && someChecked}
                  onChange={handleParentChange}
                />
              </TableCell>

              <TableCell
                sx={{
                  ...headerCellSx,
                  ...stickyHeaderCellSx("col2"),
                  minWidth: 260,
                }}
              >
                <FormControl size="small" sx={{ width: 200 }}>
                  <InputLabel shrink>Visit Name</InputLabel>
                  <Select
                    label="Visit Name"
                    value={searchObject?.visit ?? ""}
                    onChange={(e) =>
                      setSearchObject((prev) => ({
                        ...prev,
                        visit: e.target.value,
                      }))
                    }
                    displayEmpty
                  >
                    {downdownVisitsStatus?.map((visit) => (
                      <MenuItem key={visit.id} value={visit.visit_name}>
                        {renderVisitItem(visit.id)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {patientId && (
                  <>
                    <HelpIcon
                      aria-owns={openPopOver ? "mouse-over-popover" : undefined}
                      aria-haspopup="true"
                      onMouseEnter={handlePopoverOpen}
                      onMouseLeave={handlePopoverClose}
                      sx={{ ml: 0.5, fontSize: 18, cursor: "pointer" }}
                    />

                    <Popover
                      id="mouse-over-popover"
                      sx={{ pointerEvents: "none" }}
                      open={openPopOver}
                      anchorEl={anchorEl}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      onClose={handlePopoverClose}
                      disableRestoreFocus
                    >
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          ECRF Status
                        </Typography>
                        <Stack spacing={0.5} sx={{ mb: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <NotEcrfTouched sx={{ color: "grey", fontSize: 16 }} />
                            <Typography variant="body2">No ECRF Touched</Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <EcrfHasIssue sx={{ color: "red", fontSize: 16 }} />
                            <Typography variant="body2">At least one ECRF has issue</Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <AllEcrfCompleted sx={{ color: "green", fontSize: 16 }} />
                            <Typography variant="body2">All ECRFs completed</Typography>
                          </Stack>
                        </Stack>

                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Visit Status
                        </Typography>
                        <Stack spacing={0.5}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <VisitStatusIcon sx={{ color: "grey", fontSize: 12 }} />
                            <Typography variant="body2">Visit Not Opened</Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <VisitStatusIcon sx={{ color: "red", fontSize: 12 }} />
                            <Typography variant="body2">Incomplete Visit</Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <VisitStatusIcon sx={{ color: "orange", fontSize: 12 }} />
                            <Typography variant="body2">Skipped Visit</Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <VisitStatusIcon sx={{ color: "green", fontSize: 12 }} />
                            <Typography variant="body2">Completed Visit</Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    </Popover>
                  </>
                )}
              </TableCell>

              <TableCell
                sx={{
                  ...headerCellSx,
                  ...stickyHeaderCellSx("col3"),
                  minWidth: 260,
                }}
              >
                <TextField
                  label="Block Name"
                  placeholder="Search by Block Name"
                  size="small"
                  sx={{ width: 200 }}
                  InputLabelProps={{ shrink: true }}
                  value={searchObject.block_name}
                  onChange={(e) =>
                    setSearchObject((prev) => ({
                      ...prev,
                      block_name: e.target.value,
                    }))
                  }
                />
              </TableCell>

              <TableCell sx={headerCellSx} align="center">
                <TableSortLabel active={sortStatus} direction="asc" onClick={handleSort}>
                  Query Status
                </TableSortLabel>
                <Badge badgeContent={queryStatusCount} color="primary" sx={{ ml: 1 }} />
              </TableCell>

              <TableCell sx={headerCellSx} align="center">
                Delete Query
              </TableCell>

              {/* CRC/INV/SUB-INV columns */}
              <TableCell sx={{ ...headerCellSx, ...sectionStartSx }}>
                <FormControl size="small" sx={{ width: 200 }}>
                  <InputLabel shrink>Block Status</InputLabel>
                  <Select
                    label="Block Status"
                    value={searchObject.block_status}
                    onChange={(e) =>
                      setSearchObject((prev) => ({
                        ...prev,
                        block_status: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Fully Completed">Fully Completed</MenuItem>
                    <MenuItem value="InComplete">InComplete</MenuItem>
                    <MenuItem value="Range Violation">Completed + Range Violation</MenuItem>
                    <MenuItem value="Not Started">Not Started</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell sx={headerCellSx} align="center">
                Email
              </TableCell>
              <TableCell sx={headerCellSx} align="center">
                Role
              </TableCell>

              {/* CRO/DM CDQC columns */}
              <TableCell sx={{ ...headerCellSx, ...sectionStartSx }}>
                <FormControl size="small" sx={{ width: 200 }}>
                  <InputLabel shrink>Block Status</InputLabel>
                  <Select
                    label="Block Status"
                    value={searchObject.cro_block_status}
                    onChange={(e) =>
                      setSearchObject((prev) => ({
                        ...prev,
                        cro_block_status: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Complete">Complete</MenuItem>
                    <MenuItem value="InComplete">InComplete</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell sx={headerCellSx} align="center">
                Email
              </TableCell>
              <TableCell sx={headerCellSx} align="center">
                Date/Time timestamp
              </TableCell>

              {/* INV eSignature columns */}
              <TableCell sx={{ ...headerCellSx, ...sectionStartSx }}>
                <FormControl size="small" sx={{ width: 200 }}>
                  <InputLabel shrink>Block Status</InputLabel>
                  <Select
                    label="Block Status"
                    value={searchObject.inv_certified}
                    onChange={(e) =>
                      setSearchObject((prev) => ({
                        ...prev,
                        inv_certified: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Certified">Certified</MenuItem>
                    <MenuItem value="UnCertified">UnCertified</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell sx={headerCellSx} align="center">
                Email
              </TableCell>
              <TableCell sx={headerCellSx} align="center">
                Date/Time timestamp
              </TableCell>
              <TableCell sx={headerCellSx} align="center">
                E-Sign Reason
              </TableCell>

              {/* DM Freeze columns */}
              <TableCell sx={{ ...headerCellSx, ...sectionStartSx }}>
                <FormControl size="small" sx={{ width: 200 }}>
                  <InputLabel shrink>Block Status</InputLabel>
                  <Select
                    label="Block Status"
                    value={searchObject.dm_block_status}
                    onChange={(e) =>
                      setSearchObject((prev) => ({
                        ...prev,
                        dm_block_status: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Frozen">Frozen</MenuItem>
                    <MenuItem value="UnFrozen">UnFrozen</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell sx={headerCellSx} align="center">
                Email
              </TableCell>
              <TableCell sx={headerCellSx} align="center">
                Freeze timestamp
              </TableCell>
            </TableRow>
          </TableHead>

          {/* ── Body ── */}
          <TableBody
            sx={{
              "& .MuiTableRow-root:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={18} sx={{ border: 0 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            ) : (
              sortedQueryLogData.map((item, index) => (
                <TableRow key={index}>
                  {/* Select */}
                  <TableCell sx={{ ...bodyCellSx, ...stickyBodyCellSx("col1") }} align="center">
                    {item.status !== "Not Started" ? (
                      <Checkbox
                        checked={checked.length >= index + 1 ? checked[index] : false}
                        onChange={(event) => handleChildChange(event, index, item.id)}
                        inputProps={{
                          "data-testid": "block-select-checkbox",
                          "data-block-name": item.block_name,
                          "data-visit-name": item.visit_name,
                        }}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  {/* Visit Name */}
                  <TableCell sx={{ ...bodyCellSx, ...stickyBodyCellSx("col2") }} align="start">
                    {item.visit_name}
                  </TableCell>

                  {/* Block Name */}
                  <TableCell sx={{ ...bodyCellSx, ...stickyBodyCellSx("col3") }} align="start">
                    {convertFirstLetterToCapital(`${item.block_name}`)}
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleHyperLink(item)}
                      sx={{ ml: 0.5 }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>

                  {/* Query Status */}
                  <TableCell sx={bodyCellSx} align="center">
                    {item.status === "Not Started" ? (
                      "-"
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          if (item.query_status?.id) {
                            handleGenerateQueryMessage(item);
                            setShowChatModal(true);
                          } else {
                            handleGenerateQuery(item);
                            setShowRolesModal(true);
                          }
                        }}
                      >
                        {item.query_status?.id ? (
                          item.query_status?.status === "Complete" ? (
                            <CheckSharpIcon fontSize="small" />
                          ) : (
                            <QuestionMarkRoundedIcon fontSize="small" />
                          )
                        ) : (
                          "Generate a Query"
                        )}
                      </Button>
                    )}
                  </TableCell>

                  {/* Delete Query */}
                  <TableCell sx={bodyCellSx} align="center">
                    {item.query_status?.id ? (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeleteQueryId(item.query_status?.id);
                          setDeleteQuery(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  {/* CRC/INV: Block Status */}
                  <TableCell sx={{ ...bodyCellSx, ...sectionStartSx }} align="center">
                    <Box display="inline-flex" alignItems="center">
                      {item.status === "Completed" && (
                        <CheckSharpIcon sx={{ color: "green", mr: 0.5 }} fontSize="small" />
                      )}
                      {item.status === "Incomplete" && (
                        <PriorityHighIcon sx={{ color: "red", mr: 0.5 }} fontSize="small" />
                      )}
                      {item.has_constraint_error && (
                        <QuestionMarkSharpIcon sx={{ color: "red", mr: 0.5 }} fontSize="small" />
                      )}
                      {item.status === "Not Started" ? "-" : ""}
                    </Box>
                  </TableCell>

                  {/* CRC/INV: Email */}
                  <TableCell sx={bodyCellSx} align="center">
                    {item.email || "-"}
                  </TableCell>

                  {/* CRC/INV: Role */}
                  <TableCell sx={bodyCellSx} align="center">
                    {item.role || "-"}
                  </TableCell>

                  {/* CDQC: Block Status */}
                  <TableCell sx={{ ...bodyCellSx, ...sectionStartSx }} align="center">
                    {item.status_object?.cro_block_status_bool ? "Complete" : "InComplete"}
                  </TableCell>

                  {/* CDQC: Email */}
                  <TableCell sx={bodyCellSx} align="center">
                    {item.status_object?.cro_block_status || "-"}
                  </TableCell>

                  {/* CDQC: Date/Time */}
                  <TableCell sx={bodyCellSx} align="center">
                    {item.status_object?.cro_block_status &&
                    item.status_object?.cro_block_status_datetime
                      ? formatDate(
                          item.status_object.cro_block_status_datetime,
                          "ddd MMM DD YYYY, hh:mm:ss A"
                        )
                      : "-"}
                  </TableCell>

                  {/* eSign: Status */}
                  <TableCell sx={{ ...bodyCellSx, ...sectionStartSx }} align="center">
                    {item.status_object?.inv_certified_bool === true
                      ? "Certified"
                      : item.status_object?.inv_certified_bool === false
                        ? "UnCertified"
                        : "-"}
                  </TableCell>

                  {/* eSign: Email */}
                  <TableCell sx={bodyCellSx} align="center">
                    {item.status_object?.inv_certified || "-"}
                  </TableCell>

                  {/* eSign: Date/Time */}
                  <TableCell sx={bodyCellSx} align="center">
                    {item.status_object?.inv_certified && item.status_object?.inv_certified_datetime
                      ? formatDate(
                          item.status_object.inv_certified_datetime,
                          "ddd MMM DD YYYY, hh:mm:ss A"
                        )
                      : "-"}
                  </TableCell>

                  {/* eSign: Reason */}
                  <TableCell sx={bodyCellSx} align="center">
                    {item.status_object?.e_sign_reason_type || "-"}
                  </TableCell>

                  {/* Freeze: Status */}
                  <TableCell sx={{ ...bodyCellSx, ...sectionStartSx }} align="center">
                    {item.status_object?.dm_freeze_bool === true ? "Frozen" : "Unfrozen"}
                  </TableCell>

                  {/* Freeze: Email */}
                  <TableCell sx={bodyCellSx} align="center">
                    {item.status_object?.dm_freeze || "-"}
                  </TableCell>

                  {/* Freeze: Date/Time */}
                  <TableCell sx={bodyCellSx} align="center">
                    {item.status_object?.dm_freeze && item.status_object?.dm_freeze_datetime
                      ? formatDate(
                          item.status_object.dm_freeze_datetime,
                          "ddd MMM DD YYYY, hh:mm:ss A"
                        )
                      : "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={errorPopupOpen}
        onClose={() => setErrorPopupOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid="error-attention-dialog"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            color: "warning.main",
          }}
        >
          Attention
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>{errorMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorPopupOpen(false)} data-testid="error-attention-close-btn">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Query Confirmation */}
      <Dialog open={deleteQuery} onClose={() => setDeleteQuery(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this query? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteQuery(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => confirmDelete(deleteQueryId)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default QueryTableContainer;
